/**
 * Shared datetime formatting utilities.
 *
 * All formatting of UTC ISO 8601 strings must go through this module.
 * Never call toLocaleString / toLocaleDateString / toLocaleTimeString directly
 * in components, charts, or table cells.
 */

export type DateStyle = 'short' | 'medium' | 'long' | 'full';
export type TimeStyle = 'short' | 'medium' | 'long';

/**
 * Format a UTC ISO 8601 string as a locale date in the given IANA timezone.
 * e.g. formatDate("2025-04-21T14:30:00Z", "America/New_York") => "Apr 21, 2025"
 */
export function formatDate(
  iso: string,
  timezone: string,
  dateStyle: DateStyle = 'medium',
): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle, timeZone: timezone }).format(
    new Date(iso),
  );
}

/**
 * Format a UTC ISO 8601 string as a locale date and time in the given IANA timezone.
 * e.g. formatDateTime("2025-04-21T14:30:00Z", "America/New_York") => "Apr 21, 2025, 10:30 AM"
 */
export function formatDateTime(
  iso: string,
  timezone: string,
  dateStyle: DateStyle = 'medium',
  timeStyle: TimeStyle = 'short',
): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle, timeStyle, timeZone: timezone }).format(
    new Date(iso),
  );
}

/**
 * Format a UTC ISO 8601 string as a locale time only in the given IANA timezone.
 * e.g. formatTime("2025-04-21T14:30:00Z", "America/New_York") => "10:30 AM"
 */
export function formatTime(
  iso: string,
  timezone: string,
  timeStyle: TimeStyle = 'short',
): string {
  return new Intl.DateTimeFormat(undefined, { timeStyle, timeZone: timezone }).format(
    new Date(iso),
  );
}

/**
 * Convert a UTC ISO 8601 string to milliseconds since epoch.
 * Use this as the raw x-axis value for Recharts; apply formatDate/formatDateTime
 * in the tickFormatter and labelFormatter props.
 */
export function toChartTimestamp(iso: string): number {
  return new Date(iso).getTime();
}

/**
 * Convert a calendar date string (YYYY-MM-DD) from a date input to a UTC midnight
 * ISO 8601 string. Use this before submitting a date-only form field to a
 * timestamp column in the database.
 * e.g. calendarDateToUtcIso("2025-04-21") => "2025-04-21T00:00:00.000Z"
 */
export function calendarDateToUtcIso(dateString: string): string {
  return new Date(`${dateString}T00:00:00Z`).toISOString();
}

/**
 * Extract the YYYY-MM-DD portion from a UTC ISO 8601 string, in the given timezone.
 * Useful for pre-filling a date <input> from an API response.
 */
export function isoToCalendarDate(iso: string, timezone: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: timezone }).format(new Date(iso));
}

/**
 * Returns the browser's local IANA timezone identifier.
 * This is the default value for the app timezone store.
 */
export function browserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Format a `YYYYMM` string (ANEEL's `collection_year_month` and every field
 * derived from it -- ledger and row `earliest`/`latest`, a collection row's
 * own `year_month`) as `YYYY-MM`.
 *
 * There is no parsed date column behind any of these; rendered raw the value
 * reaches a reader as `200501`, a number they have to decode. One definition
 * so every screen reading this shape reads it the same way -- a case detail
 * page and a fee ledger page previously disagreed on this by each carrying
 * its own copy.
 */
export function formatYearMonth(value: string | null): string {
  if (!value || value.length !== 6) return value ?? '—';
  return `${value.slice(0, 4)}-${value.slice(4)}`;
}

/**
 * A session's date for display: the parsed date when the corpus has one, else the
 * ata's own prose, else null.
 *
 * The parsed date is a calendar DATE with no time, so it is formatted in UTC: a local
 * zone would show the day before for any viewer west of Greenwich. The prose is shown
 * as written because it is all the source says when no date could be parsed.
 */
export function formatSessionDate(
  iso: string | null | undefined,
  raw: string | null | undefined,
  locale: string,
): string | null {
  if (iso) {
    const parsed = new Date(`${iso}T00:00:00Z`);
    if (!Number.isNaN(parsed.getTime())) {
      return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeZone: 'UTC' }).format(parsed);
    }
  }
  return raw || null;
}
